import { Globe, ScheduledFilled } from 'blaze-ui/icons'
import Styles from './PublishDocModal.module.scss'
import { Modal, useModals } from 'almanac-ui/Modal'
import { ClockCheck, Send2 } from 'blaze-ui/icons/24'
import { Export } from 'almanac-ui/icons'
import { usePostStatusDropdownState } from 'components/PostStatusDropdown/usePostStatusDropdownState'
import { buttonLabelFromIntent } from 'components/PostStatusDropdown/PostStatusDropdown'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { UserIntent } from 'components/PostStatusDropdown/Types'
import { IconProps } from 'blaze-ui/icons/Types'
import { Pressable } from '@react-aria/interactions'
import { AssigneeTypeFromAssetType } from 'hooks/useAlmanacFileSubscription'
import { AlmanacFileSummary } from 'types'
import { FloatingPortal } from '@floating-ui/react-dom-interactions'
import { Popover } from 'components/Popover/Popover'
import React, { useEffect, useRef, useState } from 'react'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import dayjs from 'dayjs'
import { Button } from 'blaze-ui/Button'
import { Position } from 'css-box-model'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { IntegrationRequiredModal } from 'components/IntegrationRequiredModal/IntegrationRequiredModal'
import { SocialChannelType } from 'common/ShareSheetV2/Screens/Default/Default'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { getProviderForChannel } from 'components/ArticlePostPreview/utils'
import { IntegrationsModal } from 'components/IntegrationsModal/IntegrationsModal'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

const SECTIONS: {
  action: UserIntent
  icon: React.FunctionComponent<IconProps>
  title: string
  content: string
}[] = [
  {
    action: UserIntent.ScheduleAutoPost,
    icon: ScheduledFilled,
    title: 'Schedule to Post Automatically',
    content: 'Blaze would post this post on your behalf',
  },
  {
    action: UserIntent.PostNow,
    icon: Send2,
    title: 'Post Now',
    content: 'Blaze would post this post on your behalf',
  },
  {
    action: UserIntent.ScheduleReminder,
    icon: ClockCheck,
    title: 'Set a reminder to post',
    content: 'Get an email on the scheduled time & date',
  },
  {
    action: UserIntent.MarkAsPosted,
    icon: Globe,
    title: 'Mark as posted',
    content: 'Changes the status to “Posted” to track your content better',
  },
  {
    action: UserIntent.Export,
    icon: Export,
    title: 'Export',
    content: 'Export to Zapier, WordPress, HTML or PDF',
  },
  {
    action: UserIntent.CopyDocContent,
    icon: Export,
    title: 'Copy doc content',
    content: 'Copies all contents to your clipboard so you can paste it easily in another product.',
  },
]

export const PublishDocModal = (props: { file: AlmanacFileSummary }) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { file } = props
  const { closeModal, openModal } = useModals()
  const modalRef = useRef<HTMLDivElement>(null)
  const { isMobile } = useResponsiveDesign()
  const { data: integrationsData } = useExternalIntegrations()
  const { blogIntegrations } = useBlogIntegrations()
  const [calendarAction, setCalendarAction] = useState<UserIntent | null>(null)
  const [calendarPos, setCalendarPos] = useState<Position | null>(null)
  const [selectedDate, setSelectedDate] = useState(dayjs().hour(9).minute(0).toISOString())
  const assigneeType = AssigneeTypeFromAssetType[file.asset_type]

  const { executeAction } = usePostStatusDropdownState({
    isMobile,
    assignee: { id: file.id, type: assigneeType },
  })

  const checkConnectedIntegration = (action: UserIntent) => {
    if (action === UserIntent.ScheduleAutoPost || action === UserIntent.PostNow) {
      let hasIntegration = false

      if (file?.channel === ChannelType.BlogPost) {
        // Use blog integrations hook for blog posts
        hasIntegration = blogIntegrations.length > 0
      } else {
        // Use dynamic provider resolution for social channels
        const provider = getProviderForChannel(file?.channel as SocialChannelType)
        hasIntegration = !!integrationsData?.external_integrations.find(
          (integration) => integration.provider === provider,
        )
      }

      if (!hasIntegration) {
        if (file?.channel === ChannelType.BlogPost) {
          // For blog posts, show integrations modal instead of specific integration modal
          openModal(IntegrationsModal)
        } else {
          // For social channels, show specific integration required modal
          const provider = getProviderForChannel(file?.channel as SocialChannelType)
          openModal(IntegrationRequiredModal, {
            integration: provider as ExternalIntegrationEnum,
          })
        }
        return false
      }
    }

    return true
  }

  const handleExecute = (action: UserIntent) => {
    if (!checkConnectedIntegration(action)) {
      return
    }

    if (!calendarAction && (action === UserIntent.ScheduleAutoPost || action === UserIntent.ScheduleReminder)) {
      setCalendarAction(action)
      return
    }

    setCalendarAction(null)
    closeModal()
    executeAction(action, selectedDate)
  }

  useEffect(() => {
    const rect = modalRef.current?.getBoundingClientRect()

    if (rect) {
      setCalendarPos({
        x: rect.x + rect.width / 2,
        y: rect.y + rect.height / 2,
      })
    }
  }, [])

  return (
    <Modal.Root ref={modalRef} size="sm">
      <Modal.Header title="Choose how to publish" />
      <Modal.Content withoutFooter={isSystemRedesignEnabled}>
        <div className={Styles.contentSection}>
          {SECTIONS.map(({ title, content, icon: Icon, action }) => {
            return (
              <React.Fragment key={action}>
                <Pressable onPress={() => handleExecute(action)}>
                  <div tabIndex={0} className={Styles.card}>
                    <Icon size={25} />
                    <h5 className={Styles.header}>{title}</h5>
                    <span className={Styles.content}>{content}</span>
                  </div>
                </Pressable>

                {calendarAction === action && (
                  <FloatingPortal id="floating-ui-root">
                    <Popover
                      style={{
                        position: 'fixed',
                        top: calendarPos?.y,
                        left: calendarPos?.x,
                        transform: 'translate(-50%, -50%)',
                      }}
                      isOpen
                      onClose={() => {
                        setCalendarAction(null)
                      }}
                      isDismissable
                    >
                      <>
                        <div className={Styles.dateTimeHeader}>
                          {calendarAction === UserIntent.ScheduleAutoPost
                            ? 'Select when your content will be automatically posted.'
                            : 'Select a date and time for your reminder.'}
                        </div>
                        <DateTimePicker
                          dateTime={selectedDate ? dayjs(selectedDate) : undefined}
                          inline
                          actions={
                            <Button onPress={() => handleExecute(calendarAction)}>
                              {buttonLabelFromIntent(calendarAction)}
                            </Button>
                          }
                          setDateTime={(datetime) => {
                            if (!datetime) return
                            setSelectedDate(datetime.toISOString())
                          }}
                        />
                      </>
                    </Popover>
                  </FloatingPortal>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </Modal.Content>
    </Modal.Root>
  )
}
